import { useAuth } from '@/hooks/auth/use-auth';
import { useGetAdminPermissions } from '@/queries/hooks/profile/use-admin-permissions';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { routes } from '../config/routes';
import { studentBothRoutes } from '../config/student-routes';
import { teacherBothRoutes } from '../config/teacher-routes';
import type { GroupRoute, RouteConfig } from '../types';

export function useRoleRoutes() {
  const { user } = useAuth();
  const {
    data: permissions,
    isLoading: isAdminPermissionsLoading,
    isSuccess: isAdminPermissionsSuccess,
  } = useGetAdminPermissions(user?.username || '', user?.user_type === 'school_admin');

  const location = useLocation();

  // Determine active group based on path - only for UI purposes
  const currentActiveGroup = location.pathname.startsWith('/subjects/') ? 'subject' : 'home';

  // filter the routes based on the permissions
  const filterRoutes = (routes: RouteConfig[], perms: typeof permissions): RouteConfig[] => {
    return routes
      .map((route) => {
        if ('isLabel' in route) {
          return route;
        }

        const hasPermission = !route.permission || perms?.permissions[route.permission] === true;

        if (!hasPermission) return null;

        if ('isGroup' in route && route.isGroup && route.children) {
          const filteredChildren = filterRoutes(route.children, perms);
          if (filteredChildren.length === 0) return null;
          return { ...route, children: filteredChildren };
        }

        return route;
      })
      .filter(Boolean) as RouteConfig[];
  };

  const cleanupLabels = (rts: RouteConfig[]): RouteConfig[] => {
    const result: RouteConfig[] = [];
    for (let i = 0; i < rts.length; i++) {
      const current = rts[i];
      if ('isLabel' in current) {
        // Peek next
        if (i + 1 < rts.length && 'isLabel' in rts[i + 1]) {
          // Skip this label if next is also label
          continue;
        }
        result.push(current);
      } else if ('isGroup' in current && current.children) {
        const cleanedChildren = cleanupLabels(current.children);
        if (cleanedChildren.length > 0) {
          result.push({ ...current, children: cleanedChildren });
        }
      } else {
        result.push(current);
      }
    }
    // Remove trailing label
    if (result.length > 0 && 'isLabel' in result[result.length - 1]) {
      result.pop();
    }
    return result;
  };

  const roleRoutes = useMemo(() => {
    const userRole = user?.user_type || 'student';

    // we will have to do permission based filtering here in case of school_admin
    if (user?.user_type === 'school_admin') {
      if (!isAdminPermissionsSuccess) return []; // Return no routes until permissions are loaded
      const filtered = filterRoutes(routes[userRole as keyof typeof routes], permissions);

      return cleanupLabels(filtered);
    }
    return routes[userRole as keyof typeof routes] || [];
  }, [user?.user_type, permissions, isAdminPermissionsSuccess]);

  const flattenRoutes = useCallback((routes: RouteConfig[]): RouteConfig[] => {
    return routes.reduce((acc: RouteConfig[], route) => {
      if ('isLabel' in route) return acc;
      if ('isGroup' in route && (route as GroupRoute).children) {
        return [...acc, ...flattenRoutes((route as GroupRoute).children)];
      }
      return [...acc, route];
    }, []);
  }, []);

  const getFlattenedRoutes = useMemo(() => {
    switch (user?.user_type) {
      case 'teacher':
        return [
          ...flattenRoutes(teacherBothRoutes.home),
          ...flattenRoutes(teacherBothRoutes.subject),
        ];
      case 'student':
        return [
          ...flattenRoutes(studentBothRoutes.home),
          ...flattenRoutes(studentBothRoutes.subject),
        ];
      default:
        return flattenRoutes(roleRoutes);
    }
  }, [roleRoutes, user?.user_type, flattenRoutes]);

  const getSidebarRoutes = useMemo(() => {
    if (user?.user_type === 'teacher' || user?.user_type === 'student') {
      const bothRoutes = user.user_type === 'teacher' ? teacherBothRoutes : studentBothRoutes;
      // Only show routes for current active group in sidebar
      const routes = bothRoutes[currentActiveGroup].filter((route) => route.showOnSideBar);
      return routes.map((route) => {
        if ('children' in route) {
          return {
            ...route,
            children: route.children.filter((child) => child.showOnSideBar),
          };
        }
        return route;
      });
    }
    return roleRoutes
      .filter((route: RouteConfig) => route.showOnSideBar)
      .map((route) => {
        if ('children' in route) {
          return {
            ...route,
            children: route.children.filter((child) => child.showOnSideBar),
          };
        }
        return route;
      });
  }, [roleRoutes, currentActiveGroup, user?.user_type]);

  const getComboboxRoutes = useMemo(() => {
    return getFlattenedRoutes.filter((route) => 'isCombobox' in route && route.isCombobox === true);
  }, [getFlattenedRoutes]);

  const isLoading = user?.user_type === 'school_admin' && isAdminPermissionsLoading;

  return {
    routes: roleRoutes,
    flattenedRoutes: getFlattenedRoutes,
    sidebarRoutes: getSidebarRoutes,
    comboboxRoutes: getComboboxRoutes,
    isLoading,
  };
}
